"use client";

import type { RouterOutputs } from "@api/trpc/routers/_app";
import { Share2Icon, TrashIcon } from "@radix-ui/react-icons";
import { Loader2, MoreHorizontal, NotebookPen } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { DeleteModal } from "@/components/modals/delete-modal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteNote } from "@/features/ai-notetaker/api/use-delete-note";
import useShareNoteModal from "@/features/ai-notetaker/hooks/use-share-note-modal";
import { cn, getSlug } from "@/lib/utils";
import { useI18n } from "@/locales/client";

interface NoteCardProps {
  note: RouterOutputs["notes"]["getNoteById"];
  author?: string;
  profileImg?: string | null;
  isOwner?: boolean;
}

export const NoteCard = ({
  note,
  author,
  profileImg,
  isOwner = false,
}: NoteCardProps) => {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const deleteMutation = useDeleteNote({ id: note.id });
  const { onOpen: openShareModal } = useShareNoteModal();
  const t = useI18n();
  const noteUrl = `/notes/${getSlug(note.name, note.id)}`;
  return (
    <>
      <Link href={noteUrl} className="group">
        <Card
          className={cn(
            "flex flex-col rounded-2xl border bg-muted/40 h-[180px]",
            "hover:bg-muted/60 transition-colors",
          )}
        >
          <CardContent className="flex-grow py-6 px-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-base line-clamp-2">
                  {note.name}
                </h3>
                {isOwner && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                        onClick={(e) => e.preventDefault()}
                      >
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-40" align="start">
                      <DropdownMenuItem
                        className="flex gap-2 p-[10px]"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          openShareModal({
                            noteId: note.id,
                            noteName: note.name,
                            isPublic: note.isPublic,
                          });
                        }}
                      >
                        <Share2Icon className="size-4" />
                        <span className="text-sm">{t("share")}</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive flex gap-2 p-[10px]"
                        disabled={deleteMutation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setIsDeleteOpen(true);
                        }}
                      >
                        {deleteMutation.isPending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <TrashIcon className="size-4" />
                        )}
                        <span className="text-sm">{t("delete")}</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <Badge
                variant="accent"
                className="w-fit text-xs gap-1.5 rounded-full px-2 md:px-2 py-0.5 md:py-0.5"
              >
                <NotebookPen className="size-3" />
                {t("noteView.card.note")}
              </Badge>
            </div>
          </CardContent>
          {author && (
            <CardFooter className="p-4 pt-0">
              <div className="flex items-center gap-2">
                <Avatar className="size-7">
                  <AvatarImage
                    alt={t("noteView.card.profileImage")}
                    src={profileImg ?? undefined}
                  />
                  <AvatarFallback className="text-xs">
                    {author[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <p className="text-sm font-medium text-muted-foreground tracking-wide">
                  {author}
                </p>
              </div>
            </CardFooter>
          )}
        </Card>
      </Link>
      <DeleteModal
        title={t("noteView.card.delete.title")}
        message={t("noteView.card.delete.description")}
        handleCancel={() => setIsDeleteOpen(false)}
        handleConfirm={() => {
          deleteMutation.mutate(
            { id: note.id },
            {
              onSuccess: () => setIsDeleteOpen(false),
            },
          );
        }}
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        pending={deleteMutation.isPending}
      />
    </>
  );
};
